/**
 * Recognising a rate-limited response, wherever it was thrown from (z8nrz7cnhz).
 *
 * There is no shared API client: every `src/api/*Api.ts` has its own local
 * `request`, and they do not agree on what they throw. `processStepsApi` throws
 * an `ApiError` carrying `.status`; most of the others parse the response body
 * and re-throw a bare `Error(parsed.message)`, **dropping the status entirely**.
 *
 * For a throttled request that message is Nest's own `ThrottlerException: Too
 * Many Requests` — which is exactly how a clinician came to read an exception
 * class name where a patient's case should have been.
 *
 * So detection has to work from either shape: the status when a thrower kept it,
 * the message when it did not. The message patterns are matched narrowly rather
 * than on a loose `429` so a coincidental number in some unrelated error can
 * never be mistaken for a rate limit:
 *
 * - `ThrottlerException: …` — Nest's default 429 body message.
 * - `Too many requests …` — what this API sends since UserThrottlerGuard.
 * - `… failed (429)` — the fallback format in `proceduresApi` and friends.
 *
 * The right long-term fix is one API client that preserves status everywhere;
 * until then this reads both dialects.
 */

/** Copy shown to the user in place of any rate-limit error. */
export const RATE_LIMIT_MESSAGE =
  'The server is handling a lot of requests right now. Give it a moment and try again.'

const RATE_LIMIT_PATTERNS = [/throttlerexception/i, /too many requests/i, /failed \(429\)/i]

/** A numeric `status` if the thrower kept one — `ApiError` does, most do not. */
export function statusOf(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined
  const status = (err as { status?: unknown }).status
  return typeof status === 'number' ? status : undefined
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message
  return typeof err === 'string' ? err : ''
}

/** Was this error the rate limiter refusing the request? */
export function isRateLimited(err: unknown): boolean {
  if (statusOf(err) === 429) return true
  const message = messageOf(err)
  return RATE_LIMIT_PATTERNS.some(pattern => pattern.test(message))
}

/**
 * An error message fit to render.
 *
 * Rate limits get replaced outright — the raw text is an internal class name and
 * says nothing a user can act on. Everything else keeps its own message, because
 * these are mostly server validation messages worth reading verbatim.
 */
export function friendlyErrorMessage(err: unknown, fallback: string): string {
  if (isRateLimited(err)) return RATE_LIMIT_MESSAGE
  const message = messageOf(err)
  return message || fallback
}
